/**
 * Core API - Customer Complaints
 *
 * Database operations for customer complaint tracking.
 * Supports workflow: draft → complaint_recorded → in_progress → closed.
 */

import { supabase } from '@/integrations/supabase/client';

// =============================================================================
// Types
// =============================================================================

export type ComplaintStatus = 'draft' | 'complaint_recorded' | 'in_progress' | 'closed';

export interface CustomerComplaint {
  id: string;
  tenant_id: string;
  factory_id: string;
  customer_id: string;
  status: ComplaintStatus;
  complaint_date: string;
  complaint_identifier: string | null;
  comment: string | null;
  serial_number: string | null;
  production_date: string | null;
  area_id: string | null;
  line_id: string | null;
  station_id: string | null;
  article_id: string | null;
  eight_d_claim_id: string | null;
  created_by: string | null;
  created_at: string;
  updated_at: string;
}

export interface ComplaintWithRelations extends CustomerComplaint {
  customer?: { id: string; name: string; internal_code: string | null } | null;
  article?: { id: string; code: string; name: string | null } | null;
  area?: { id: string; name: string } | null;
  line?: { id: string; name: string } | null;
  station?: { id: string; name: string } | null;
  created_by_profile?: { id: string; full_name: string; email: string } | null;
}

export interface CreateComplaintInput {
  tenant_id: string;
  factory_id: string;
  customer_id: string;
  complaint_date: string;
  status?: ComplaintStatus;
  complaint_identifier?: string | null;
  comment?: string | null;
  serial_number?: string | null;
  production_date?: string | null;
  area_id?: string | null;
  line_id?: string | null;
  station_id?: string | null;
  article_id?: string | null;
  created_by?: string | null;
}

export interface ComplaintStats {
  today: number;
  thisWeek: number;
  byStatus: Record<ComplaintStatus, number>;
  topArticles: Array<{ article_id: string; article_code: string; article_name: string | null; count: number }>;
  byArea: Array<{ area_id: string; area_name: string; count: number }>;
}

export interface ComplaintAttachment {
  id: string;
  tenant_id: string;
  factory_id: string;
  complaint_id: string;
  file_name: string;
  file_path: string;
  file_size: number | null;
  file_type: string | null;
  label: string | null;
  uploaded_by: string | null;
  created_at: string;
}

// =============================================================================
// Queries - Complaints
// =============================================================================

/**
 * Fetches all complaints for a factory with relations.
 */
export async function fetchComplaintsByFactory(
  factoryId: string,
  filters?: {
    status?: ComplaintStatus;
    customerId?: string;
    startDate?: string;
    endDate?: string;
  }
): Promise<ComplaintWithRelations[]> {
  let query = supabase
    .schema('core')
    .from('customer_complaints')
    .select(`
      *,
      customer:customers(id, name, internal_code),
      article:articles(id, code, name),
      area:factory_areas(id, name),
      line:production_lines(id, name),
      station:stations(id, name),
      created_by_profile:profiles!customer_complaints_created_by_fkey(id, full_name, email)
    `)
    .eq('factory_id', factoryId);

  if (filters?.status) {
    query = query.eq('status', filters.status);
  }

  if (filters?.customerId) {
    query = query.eq('customer_id', filters.customerId);
  }

  if (filters?.startDate) {
    query = query.gte('complaint_date', filters.startDate);
  }

  if (filters?.endDate) {
    query = query.lte('complaint_date', filters.endDate);
  }

  query = query.order('complaint_date', { ascending: false });

  const { data, error } = await query;
  if (error) throw error;
  return data as unknown as ComplaintWithRelations[];
}

/**
 * Fetches a single complaint by ID with all relations.
 */
export async function fetchComplaintById(complaintId: string): Promise<ComplaintWithRelations | null> {
  const { data, error } = await supabase
    .schema('core')
    .from('customer_complaints')
    .select(`
      *,
      customer:customers(id, name, internal_code, contact_email),
      article:articles(id, code, name, description),
      area:factory_areas(id, name),
      line:production_lines(id, name),
      station:stations(id, name),
      created_by_profile:profiles!customer_complaints_created_by_fkey(id, full_name, email)
    `)
    .eq('id', complaintId)
    .maybeSingle();

  if (error) throw error;
  return data as unknown as ComplaintWithRelations | null;
}

/**
 * Fetches complaint statistics for dashboard KPIs.
 */
export async function fetchComplaintStats(factoryId: string): Promise<ComplaintStats> {
  const today = new Date().toISOString().split('T')[0];
  const weekAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];

  // Fetch all complaints for the week
  const { data: complaints, error } = await supabase
    .schema('core')
    .from('customer_complaints')
    .select(`
      id,
      complaint_date,
      status,
      area_id,
      article_id,
      area:factory_areas(id, name),
      article:articles(id, code, name)
    `)
    .eq('factory_id', factoryId)
    .gte('complaint_date', weekAgo);

  if (error) throw error;

  // Calculate stats
  const todayCount = complaints.filter(c => c.complaint_date === today).length;
  const weekCount = complaints.length;

  const byStatus: Record<ComplaintStatus, number> = {
    draft: 0,
    complaint_recorded: 0,
    in_progress: 0,
    closed: 0,
  };

  complaints.forEach((c) => {
    byStatus[c.status as ComplaintStatus] = (byStatus[c.status as ComplaintStatus] || 0) + 1;
  });

  // Top articles (most complained)
  type ComplaintForStats = typeof complaints[0];
  const articleMap = new Map<string, { code: string; name: string | null; count: number }>();
  complaints.forEach((c: ComplaintForStats) => {
    if (c.article_id && c.article) {
      const existing = articleMap.get(c.article_id) || { code: c.article.code, name: c.article.name, count: 0 };
      existing.count += 1;
      articleMap.set(c.article_id, existing);
    }
  });

  const topArticles = Array.from(articleMap.entries())
    .map(([id, data]) => ({ article_id: id, article_code: data.code, article_name: data.name, count: data.count }))
    .sort((a, b) => b.count - a.count)
    .slice(0, 5);

  // By area
  const areaMap = new Map<string, { name: string; count: number }>();
  complaints.forEach((c: ComplaintForStats) => {
    if (c.area_id && c.area) {
      const existing = areaMap.get(c.area_id) || { name: c.area.name, count: 0 };
      existing.count += 1;
      areaMap.set(c.area_id, existing);
    }
  });

  const byArea = Array.from(areaMap.entries())
    .map(([id, data]) => ({ area_id: id, area_name: data.name, count: data.count }))
    .sort((a, b) => b.count - a.count);

  return {
    today: todayCount,
    thisWeek: weekCount,
    byStatus,
    topArticles,
    byArea,
  };
}

// =============================================================================
// Queries - Attachments
// =============================================================================

/**
 * Fetches all attachments for a complaint.
 */
export async function fetchComplaintAttachments(complaintId: string): Promise<ComplaintAttachment[]> {
  const { data, error } = await supabase
    .schema('core')
    .from('customer_complaint_attachments')
    .select('*')
    .eq('complaint_id', complaintId)
    .order('created_at', { ascending: true });

  if (error) throw error;
  return data;
}

// =============================================================================
// Mutations - Complaints
// =============================================================================

/**
 * Creates a new complaint.
 */
export async function createComplaint(input: CreateComplaintInput): Promise<CustomerComplaint> {
  const { data, error } = await supabase
    .schema('core')
    .from('customer_complaints')
    .insert({
      ...input,
      status: input.status || 'draft',
    })
    .select()
    .single();

  if (error) throw error;
  return data as CustomerComplaint;
}

/**
 * Updates an existing complaint.
 */
export async function updateComplaint(
  complaintId: string,
  updates: Partial<Omit<CreateComplaintInput, 'tenant_id' | 'factory_id'>>
): Promise<CustomerComplaint> {
  const { data, error } = await supabase
    .schema('core')
    .from('customer_complaints')
    .update(updates)
    .eq('id', complaintId)
    .select()
    .single();

  if (error) throw error;
  return data as CustomerComplaint;
}

/**
 * Updates complaint status only.
 */
export async function updateComplaintStatus(
  complaintId: string,
  status: ComplaintStatus
): Promise<void> {
  const { error } = await supabase
    .schema('core')
    .from('customer_complaints')
    .update({ status })
    .eq('id', complaintId);

  if (error) throw error;
}

/**
 * Links a complaint to an 8D claim.
 */
export async function linkComplaintTo8D(
  complaintId: string,
  eightDClaimId: string
): Promise<void> {
  const { error } = await supabase
    .schema('core')
    .from('customer_complaints')
    .update({ eight_d_claim_id: eightDClaimId, status: 'in_progress' })
    .eq('id', complaintId);

  if (error) throw error;
}

/**
 * Deletes a complaint (hard delete).
 */
export async function deleteComplaint(complaintId: string): Promise<void> {
  const { error } = await supabase
    .schema('core')
    .from('customer_complaints')
    .delete()
    .eq('id', complaintId);

  if (error) throw error;
}

// =============================================================================
// Mutations - Attachments
// =============================================================================

/**
 * Adds an attachment to a complaint.
 */
export async function addComplaintAttachment(
  complaintId: string,
  tenantId: string,
  factoryId: string,
  fileName: string,
  filePath: string,
  fileSize: number,
  fileType: string,
  label?: string | null,
  uploadedBy?: string | null
): Promise<ComplaintAttachment> {
  const { data, error } = await supabase
    .schema('core')
    .from('customer_complaint_attachments')
    .insert({
      complaint_id: complaintId,
      tenant_id: tenantId,
      factory_id: factoryId,
      file_name: fileName,
      file_path: filePath,
      file_size: fileSize,
      file_type: fileType,
      label,
      uploaded_by: uploadedBy,
    })
    .select()
    .single();

  if (error) throw error;
  return data;
}

/**
 * Deletes an attachment (hard delete).
 */
export async function deleteComplaintAttachment(attachmentId: string): Promise<void> {
  const { error } = await supabase
    .schema('core')
    .from('customer_complaint_attachments')
    .delete()
    .eq('id', attachmentId);

  if (error) throw error;
}

// =============================================================================
// Storage Operations
// =============================================================================

const COMPLAINT_BUCKET_NAME = 'complaint-attachments';

/**
 * Uploads a file to the complaint attachments bucket.
 */
export async function uploadComplaintFile(
  filePath: string,
  file: File
): Promise<string> {
  const { error } = await supabase.storage
    .from(COMPLAINT_BUCKET_NAME)
    .upload(filePath, file);

  if (error) throw error;
  return filePath;
}

/**
 * Deletes a file from the complaint attachments bucket.
 */
export async function deleteComplaintFile(filePath: string): Promise<void> {
  const { error } = await supabase.storage
    .from(COMPLAINT_BUCKET_NAME)
    .remove([filePath]);

  if (error) throw error;
}

/**
 * Gets the public URL for a complaint attachment.
 */
export function getComplaintFileUrl(filePath: string): string {
  const { data } = supabase.storage
    .from(COMPLAINT_BUCKET_NAME)
    .getPublicUrl(filePath);

  return data.publicUrl;
}
